import { Maybe } from '@zaptic-external/ts-monads'
import * as React from 'react'
import type { CommonDeps } from '../base/CommonDeps'
import { getValidationMessage } from '../flow/validationMessage'
import { never } from '../util/never'
import { ElementCalculation } from '../flow/ElementCalculation'
import { ElementCheckbox } from '../flow/ElementCheckbox'
import { ElementConfirmation } from '../flow/ElementConfirmation'
import { ElementDateTime } from '../flow/ElementDateTime'
import { ElementImageContent } from '../flow/ElementImageContent'
import { ElementImageSelect } from '../flow/ElementImageSelect'
import { ElementNumber } from '../flow/ElementNumber'
import { ElementCamera } from '../flow/ElementCamera'
import { ElementRadio } from '../flow/ElementRadio'
import { ElementSection } from '../flow/ElementSection'
import { ElementSignature } from '../flow/ElementSignature'
import { ElementTextCapture } from '../flow/ElementTextCapture'
import { ElementTextContent } from '../flow/ElementTextContent'
import { ElementYesNo } from '../flow/ElementYesNo'
import { ElementDropdown } from './ElementDropdown'
import { ElementMultiselectDropdown } from './ElementMultiselectDropdown'
import { ElementUnitSelect } from './ElementUnitSelect'
import { ElementLink } from '../flow/ElementLink'
import { ElementSubtask, type SubtaskReport } from '../flow/ElementSubtask'
import type { AppFileGuideDetails, AppGuide, ParentInfo } from '../guide/Model'
import type { KnownUnit } from '../unit/unit'
import * as Flows from '../../../shared/domain/flows/types.no-deps'
import type * as Guide from '../../../shared/domain/guides/types.no-deps'
import type { HistoricalReport, ViewablePageReport } from '../../../shared/domain/viewableReports/types.no-deps'
import {
    type CreateAnswer,
    type CreateAnswerBase,
    findAnswer,
    isAnswerPredicate,
    sortChronologicallyDescending,
} from '../../../shared/domain/flows/answer.no-deps'
import { ElementFilesSelect } from './ElementFilesSelect'
import { ExecutionPageOriginatingReportHistory } from './ExecutionPageOriginatingReportHistory'
import type { FileId, FileMetaApp } from '../../../shared/domain/files/types.no-deps'
import {
    findCalculationResult,
    getElementFailures,
    getIsInProgress,
    hasStepInSummary,
} from '../../../shared/domain/flows/summary.no-deps'
import { getVisibleElementsInStep } from '../../../shared/domain/flows/element.no-deps'
import type { Upload } from '../upload/Upload'
import type { FlowUnitIds } from '../../../shared/domain/flows/FlowUnitsIds.no-deps'
import { ElementVideoContent } from './ElementVideoContent'
import { findInferredSubtaskSubjectUnit } from './SubjectType'
import { findById } from '../../../shared/lib/functional/identifiable.no-deps'
import type { Profile } from '../user/Profile'
import { getElementChoiceLookup } from '../../../shared/domain/flows/choice.no-deps'
import type { Dictionary } from '../../../shared/lib/functional/dictionary.no-deps'
import type { FileExtension } from '../../../shared/domain/files/fileExtensions.no-deps'
import type { FileUploadData } from '../upload/FileUploadData'
import type { RenderCachedImage, WithCachedImageUrl } from '../image/WithCachedImage'
import type { TimelineDetails } from './TimelineDetails'
import type { DeviceIndexLookup } from '../photo/DeviceIndexLookup'
import type { AppUnitType, UnitData } from '../../../shared/domain/units/types.no-deps'
import type { BarcodeResult } from '../unit/barcodeTypes'
import { renderPromise } from '../util/PromiseRenderer'
import type { ClientLogger } from '../../../shared/lib/ClientLogger.no-deps'
import type { FileUrlStates } from '../file/FileUrlManager'
import { type PromiseState, initialPromiseState, promiseStateRender } from '../util/PromiseState'
import type { PromiseThunkQueue } from '../../../shared/lib/PromiseThunkQueue.no-deps'
import type { WorkflowType } from '../../../shared/domain/workflows/types.no-deps'
import { type LoopPath, type LoopPathIds, toLoopPath } from '../../../shared/domain/flows/LoopPath.no-deps'
import type { LoopPlan } from '../../../shared/domain/flows/LoopPlan.no-deps'
import { ElementLoop } from './ElementLoop'
import type { FeatureFlag } from '../../../shared/domain/status/types.no-deps'
import type { CachedUnitData } from '../../../shared/domain/units/CachedUnitData.no-deps'
import { StepSubject } from './StepSubject'
import { reverse } from '../../../shared/lib/functional/array.no-deps'
import { pipe } from '../../../shared/lib/functional/pipe.no-deps'
import { ElementGuide } from './ElementGuide'
import { tryScrollToElement } from './element'
import { assertExhaustion, type UUID } from '../../../shared/lib/typeHelpers.no-deps'
import { filterGuidesByTags } from './filterGuides'
import { cancelPromiseManagers, createPromiseManager } from '../util/PromiseManager'
import { createStateLens } from '../util/react'
import type { CamelCaseFlags } from '../../../shared/cfg/flags.no-deps'
import type { UnitWorkflowParameter } from '../../../shared/domain/UnitWorkflowParameter/types.no-deps'

interface BaseProps {
    allowImageZoom: boolean
    allUnitsState: PromiseState<KnownUnit[]>
    answers: Flows.Answer[]
    barcodeScanForUnits?: {
        scan: (units: KnownUnit[]) => Promise<BarcodeResult>
        fetchUnitType: (id: number) => Promise<AppUnitType>
    }
    browserCameraDeviceIndexLookup: DeviceIndexLookup
    cachedUnitData: CachedUnitData
    cancelUpload: (fileId: FileId, logger: ClientLogger) => Promise<void>
    createAnswer: (answer: Flows.Answer) => Promise<void>
    createAnswerBase: CreateAnswerBase
    removeAnswers: (answers: Flows.Answer[]) => Promise<void>
    createSubtask: (args: CreateSubtaskArgs) => Promise<Flows.ReportId>
    cordovaGetMedia?: (type: 'camera' | 'gallery', cameraType: Flows.CameraType) => Promise<string>
    deleteSubtask: (reportId: Flows.ReportId) => Promise<void>
    deps: CommonDeps
    elementToScrollToDetails: ElementToScrollToDetails
    featureEnabled: (featureFlag: FeatureFlag) => boolean
    hasAccessToProcedure: (procedureId: number) => Promise<boolean>
    featureFlags: Pick<CamelCaseFlags, 'reportsTranslation' | 'useNewElementNumberInput'>
    fetchReportHistory: () => Promise<HistoricalReport[]>
    fetchSyncGuides: (guideIds: Guide.Id[], tagIds?: number[]) => Promise<Guide.SyncGuide[]>
    fetchUnitsByIds: (unitIds: number[]) => Promise<KnownUnit[]>
    fetchUnitsByType: (unitTypes: number[]) => Promise<KnownUnit[]>
    fetchUnitType: (unitTypeId: number) => Promise<AppUnitType | null>
    fetchUnitTypeName: (unitTypeId: number) => Promise<Maybe<string>>
    fetchOriginatingReportsCount: () => Promise<number>
    fetchOriginViewReportData?: () => Promise<ViewablePageReport>
    fileUrlStates: FileUrlStates
    filesMetaDataMap: Map<FileId, FileMetaApp>
    flowSummary: Flows.FlowSummary
    flowUnitIds: FlowUnitIds
    forceUseBrowserCameraAPI?: Promise<boolean>
    isIOS?: boolean
    isAndroid?: boolean
    loopPathIds: LoopPathIds
    loopPlan: LoopPlan
    guideId: Guide.Id
    scheduleParameters: UnitWorkflowParameter[]
    onLinkedGuidesToStep: (syncGuides: Guide.SyncGuide[]) => void
    onOpenFile: (downloadUrl: string, fileExtension: FileExtension, name: string) => Promise<void>
    onShowPreviewChange?: (previewOpen: boolean) => void
    progenitorId: Flows.ReportId
    promiseThunkQueue: PromiseThunkQueue
    readBlob: (fileId: FileId) => Promise<Blob>
    recovery?: { elementId: Flows.ElementId; value: unknown; clear: () => void }
    renderCachedImage: RenderCachedImage
    onClickGuideParentInfo: (parentInfo: ParentInfo) => void
    reportEditDetails: {
        reportCompletedDate: Date
        userProfiles: Profile[]
    } | null
    reportId: Flows.ReportId
    originReportId?: number
    reportStartDate: Date
    routeToPostPage: (postId: UUID) => void
    routeToReportPage: (reportId: number) => void
    routeToGuideExecutionPage: (
        guideId: Guide.Id,
        opts: {
            guideElementId: Flows.ElementId
            parentClientId: Flows.ReportId
            parentScrollTop?: number
            loopPathIds?: LoopPathIds
            guideId?: Guide.Id
        }
    ) => void
    routeToSection: (sectionId: Flows.ElementId, loopPathId: LoopPathIds) => void
    routeToSubtask: (reportId: Flows.ReportId) => void
    saveBlob: (blob: Blob) => Promise<FileUploadData>
    saveFile?: (fileId: FileId, path: string) => Promise<void>
    savePathForRecovery: (elementId: Flows.ElementId) => Promise<void>
    selectFile: (
        fileExtensions: FileExtension[],
        isIOS?: boolean,
        type?: 'image' | 'video'
    ) => Promise<FileUploadData[]>
    showFileProgress: boolean
    showValidation: boolean
    subjectUnit: KnownUnit
    subtaskReports: Map<Flows.ReportId, SubtaskReport>
    uploads: Upload[]
    withCachedImageUrl: WithCachedImageUrl
    workflows: Workflow[]
    footer?: React.ReactElement<unknown>
    getScrollPosition: () => number
    openOptions?: (element: Flows.Element & { loopPath: LoopPath }) => void
}

interface Props extends BaseProps {
    fetchElementGuides: () => Promise<AppGuide[]>
    fetchFileGuideDetailsMap: (fileIds: FileId[]) => Promise<Map<FileId, AppFileGuideDetails>>
    steps: Flows.Step[]
}

interface OriginatingReportsDataState {
    originatingReportsCount: number
    originViewableReport: ViewablePageReport | null
}

interface State {
    elementGuideContext: Maybe<Promise<ElementGuideContext>>
    originatingReportsDataState: PromiseState<OriginatingReportsDataState>
}

export interface CreateSubtaskArgs {
    procedureId: number
    workflowId: number
    parentClientId: Flows.ReportId
    progenitorClientId: Flows.ReportId
    subjectUnitId: number
    parentSubjectUnitId: number
}

export interface ElementToScrollTo {
    elementId: Flows.ElementId
    loopPath: LoopPath
    behavior: ScrollBehavior
}

export interface ElementToScrollToDetails {
    elementToScrollTo: ElementToScrollTo
    clearElementScroll: () => void
}

export interface Workflow {
    id: number
    name: string
    flow: Flows.Flow
    isDeleted: boolean
    type: WorkflowType
}

interface ElementGuideContext {
    fileGuideDetailsMap: Map<FileId, AppFileGuideDetails>
    guidesMap: Map<Guide.Id, AppGuide>
}

interface PrivateProps extends BaseProps {
    elementGuideContext: Maybe<Promise<ElementGuideContext>>
    fetchFileGuideDetailsMap: (fileIds: FileId[]) => Promise<Map<FileId, AppFileGuideDetails>>
    subjectData: UnitData
}

interface StepsProps extends PrivateProps {
    steps: Flows.Step[]
    isLoop?: boolean
}

interface ElementsProps extends PrivateProps {
    step: Flows.Step
}

interface ElementProps extends PrivateProps {
    element: Flows.Element
}

export class ExecutionPageBody extends React.Component<Props, State> {
    public state: State = {
        elementGuideContext: Maybe.nothing(),
        originatingReportsDataState: initialPromiseState,
    }

    public promiseManagers = {
        originatingReportsData: createPromiseManager(
            () => this.props.deps,
            createStateLens(this, 'originatingReportsDataState')
        ),
    }

    private fetchOriginViewableReport = async () => {
        if (!this.props.originReportId) {
            return null
        }

        return this.props.fetchOriginViewReportData()
    }

    private fetchOriginViewReportData = async () => {
        return this.promiseManagers.originatingReportsData.trigger(async () => {
            const [originatingReportsCount, originViewableReport] = await Promise.all([
                this.props.fetchOriginatingReportsCount(),
                this.fetchOriginViewableReport(),
            ])

            return { originatingReportsCount, originViewableReport }
        })
    }

    public componentDidMount(): void {
        const elementGuideContext = Maybe.just(this.fetchElementGuideContext())

        this.fetchOriginViewReportData()

        this.setState({ elementGuideContext })
    }

    public componentWillUnmount() {
        cancelPromiseManagers(this.promiseManagers)
    }

    public render(): JSX.Element {
        return (
            <>
                <div className="AppCore__flow__ExecutionPageBody">
                    {this.promiseManagers.originatingReportsData.render((data) => (
                        <ExecutionPageOriginatingReportHistory
                            deps={this.props.deps}
                            expectedReportCount={data.originatingReportsCount}
                            featureFlags={this.props.featureFlags}
                            fetchReportHistory={this.props.fetchReportHistory}
                            onOpenFile={this.props.onOpenFile}
                            renderCachedImage={this.props.renderCachedImage}
                            routeToPostPage={this.props.routeToPostPage}
                            routeToReportPage={this.props.routeToReportPage}
                        />
                    ))}
                    <Steps
                        {...this.props}
                        elementGuideContext={this.state.elementGuideContext}
                        steps={this.props.steps}
                        subjectData={this.props.subjectUnit?.data}
                        openOptions={this.props.openOptions}
                    />
                </div>

                {this.props.footer && (
                    <div className="AppCore__flow__ExecutionPageBody__footer">{this.props.footer}</div>
                )}
            </>
        )
    }

    // todo - maybe need to changer it here and filter out ones we dont want
    private fetchElementGuideContext = async (): Promise<ElementGuideContext> => {
        const guides = await this.props.fetchElementGuides()
        const fileIds = guides.filter((guide) => guide.variant === 'file').map((guide) => guide.file)
        const fileGuideDetailsMap = await this.props.fetchFileGuideDetailsMap(fileIds)
        const guidesMap = new Map(guides.map((guide) => [guide.id, guide]))

        return { fileGuideDetailsMap, guidesMap }
    }
}

function isLoopSubjectFilter(guidesDynamicFilter: Flows.GuidesDynamicFilter): guidesDynamicFilter is 'loopSubject' {
    return guidesDynamicFilter === 'loopSubject'
}

function isReportSubjectFilter(guidesDynamicFilter: Flows.GuidesDynamicFilter): guidesDynamicFilter is 'reportSubject' {
    return guidesDynamicFilter === 'reportSubject'
}

function getFilteredGuides({
    step,
    subjectUnit,
    syncedGuides,
    loopPathIds,
}: StepsProps & { step: Flows.ConcreteStep; syncedGuides: Guide.SyncGuide[] }) {
    const allGuidesForStep = syncedGuides.filter((guide) => step.guides?.includes(guide.id))
    if (!step.guidesDynamicFilter) {
        return allGuidesForStep
    }

    if (isReportSubjectFilter(step.guidesDynamicFilter)) {
        return allGuidesForStep.filter((guide) => guide.units.includes(subjectUnit?.id))
    }

    if (isLoopSubjectFilter(step.guidesDynamicFilter)) {
        /* Once inside the loop, there should only be one subject inside the loopPathIds array */
        const loopSubject = loopPathIds[0]
        return allGuidesForStep.filter((guide) => guide.units.includes(loopSubject.unitId))
    }

    assertExhaustion(step.guidesDynamicFilter, 'Unexpected guides dynamic filter:')
}

function Steps(props: StepsProps) {
    const { elementToScrollTo, clearElementScroll } = props.elementToScrollToDetails

    // Scroll to desired element after render is done.
    if (elementToScrollTo) {
        React.useEffect(() => {
            tryScrollToElement(
                elementToScrollTo.elementId,
                elementToScrollTo.loopPath,
                elementToScrollTo.behavior,
                4,
                false
            )

            clearElementScroll()
        }, [])
    }

    return (
        <div className="AppCore__flow__ExecutionPageBody__steps">
            {props.steps
                .filter(Flows.isConcreteStep)
                .filter((step) => hasStepInSummary(props.flowSummary, step.id, props.loopPathIds))
                .filter((step) => step.elements.length > 0 || step.label.trim().length > 0)
                .map((step) => (
                    <div className="AppCore__flow__ExecutionPageBody__step" key={step.id}>
                        {props.isLoop ? (
                            <LoopStepHeader {...props} step={step} />
                        ) : (
                            <StepHeader {...props} step={step} />
                        )}
                        <div className="AppCore__flow__ExecutionPageBody__elements">
                            <Elements {...props} step={step} />
                        </div>
                    </div>
                ))}
        </div>
    )
}

function StepHeader(props: StepsProps & { step: Flows.ConcreteStep }) {
    const hasStepGuides = props.step?.guides?.length > 0

    return (
        (props.step.label || hasStepGuides) && (
            <div className="AppCore__flow__ExecutionPageBody__step-header">
                <div className="AppCore__flow__ExecutionPageBody__step-label">{props.step.label}</div>
                <StepSubject
                    deps={props.deps}
                    allUnitsState={props.allUnitsState}
                    withCachedImageUrl={props.withCachedImageUrl}
                    loopPathIds={props.loopPathIds}
                    size="small"
                />
                <LinkedGuidesButton {...props} step={props.step} />
            </div>
        )
    )
}

function LoopStepHeader(props: StepsProps & { step: Flows.ConcreteStep }) {
    const hasStepGuides = props.step?.guides?.length > 0
    return (
        (props.step.label || hasStepGuides) && (
            <div className="AppCore__flow__ExecutionPageBody__loop-step-header">
                <div className="AppCore__flow__ExecutionPageBody__loop-step-header-upper">
                    <div className="AppCore__flow__ExecutionPageBody__loop-step-label">{props.step.label}</div>
                    <LinkedGuidesButton {...props} step={props.step} />
                </div>
            </div>
        )
    )
}

function LinkedGuidesButton(props: StepsProps & { step: Flows.ConcreteStep }) {
    const { fetchSyncGuides, step, deps } = props

    const guideIds = step.guides || []

    if (guideIds.length === 0) {
        return null
    }

    return (
        <div className="AppCore__flow__ExecutionPageBody__step-linked-guides-btn-wrapper">
            {renderPromise(
                deps,
                `LinkedGuidesBtn-${step.id}`,
                () => fetchSyncGuides(guideIds),
                (syncedGuides) => (
                    <FilteredGuidesButton {...props} syncedGuides={syncedGuides} />
                )
            )}
        </div>
    )
}

function FilteredGuidesButton(props: StepsProps & { step: Flows.ConcreteStep; syncedGuides: Guide.SyncGuide[] }) {
    const { onLinkedGuidesToStep } = props

    const filteredGuides = getFilteredGuides(props)

    if (filteredGuides.length === 0) {
        return null
    }

    return (
        <button
            className="AppCore__flow__ExecutionPageBody__step-linked-guide-btn"
            onClick={() => onLinkedGuidesToStep(filteredGuides)}
        >
            <zaptic-icon icon-name="guide" />
        </button>
    )
}

function Elements(props: ElementsProps) {
    const visibleElements = getVisibleElementsInStep(props.step, props.flowSummary, props.loopPathIds)

    return (
        <>
            {visibleElements.map((element) => (
                <Element {...props} key={element.id} element={element} />
            ))}
        </>
    )
}

function Element(props: ElementProps) {
    const { element, loopPathIds, reportEditDetails } = props
    const answer = findAnswer(props.flowSummary.answers, element.id, loopPathIds)

    let choicesLookup: Dictionary<string> | null = null
    let timelineDetails: TimelineDetails = null

    if (reportEditDetails !== null) {
        // Filter all answers for the element
        const elementAnswers = pipe(
            props.answers.filter(isAnswerPredicate(element.id, loopPathIds)),
            sortChronologicallyDescending,
            reverse
        )

        const hasBeenEdited =
            reportEditDetails.reportCompletedDate != null &&
            elementAnswers.some((a) => a.timestamp.getTime() > reportEditDetails.reportCompletedDate.getTime())

        const userProfiles = reportEditDetails.userProfiles

        choicesLookup = getElementChoiceLookup(element)
        timelineDetails = { elementAnswers, choicesLookup, hasBeenEdited, userProfiles }
    }

    const createAnswer: CreateAnswer<Flows.Answer> = (fn) =>
        props.createAnswer(fn(props.createAnswerBase(element.id, loopPathIds, answer)))

    const validationMessages: string[] = []
    if (props.showValidation) {
        for (const failure of getElementFailures(props.flowSummary, element.id, loopPathIds)) {
            validationMessages.push(getValidationMessage(element, failure.reason))
        }
    }

    const openOptions =
        props.openOptions && (() => props.openOptions({ ...element, loopPath: toLoopPath(loopPathIds) }))

    switch (element.type) {
        case 'yes-no':
            return (
                <ElementYesNo
                    deps={props.deps}
                    key={element.id}
                    elementId={element.id}
                    loopPathIds={loopPathIds}
                    label={element.label}
                    timelineDetails={timelineDetails}
                    required={element.required}
                    useIcons={element.useIcons || false}
                    iconSize={element.iconSize || 'small'}
                    yesWeight={element.yesWeight ?? 1}
                    noWeight={element.noWeight ?? 0}
                    validationMessages={validationMessages}
                    createAnswer={createAnswer}
                    answerValue={answer?.type === 'yes-no' ? answer?.value : null}
                    openOptions={openOptions}
                />
            )
        case 'number':
            return (
                <ElementNumber
                    deps={props.deps}
                    key={element.id}
                    elementId={element.id}
                    loopPathIds={loopPathIds}
                    label={element.label}
                    timelineDetails={timelineDetails}
                    required={element.required}
                    unitOfMeasurement={element.unitOfMeasurement}
                    validationMessages={validationMessages}
                    createAnswer={createAnswer}
                    answerValue={answer?.type === 'number' ? answer?.value : null}
                    openOptions={openOptions}
                    featureFlags={props.featureFlags}
                />
            )

        case 'radio':
            return (
                <ElementRadio
                    deps={props.deps}
                    key={element.id}
                    elementId={element.id}
                    loopPathIds={loopPathIds}
                    label={element.label}
                    timelineDetails={timelineDetails}
                    required={element.required}
                    choices={element.choices}
                    validationMessages={validationMessages}
                    createAnswer={createAnswer}
                    answerValue={answer?.type === 'radio' ? answer?.value : null}
                    openOptions={openOptions}
                />
            )
        case 'checkbox':
            return (
                <ElementCheckbox
                    deps={props.deps}
                    key={element.id}
                    elementId={element.id}
                    loopPathIds={loopPathIds}
                    label={element.label}
                    timelineDetails={timelineDetails}
                    required={element.required}
                    choices={element.choices}
                    maxChoices={element.maxChoices}
                    validationMessages={validationMessages}
                    createAnswer={createAnswer}
                    answerValue={answer?.type === 'checkbox' ? answer?.value : null}
                    openOptions={openOptions}
                />
            )
        case 'image-select':
            return (
                <ElementImageSelect
                    deps={props.deps}
                    key={element.id}
                    elementId={element.id}
                    loopPathIds={loopPathIds}
                    label={element.label}
                    timelineDetails={timelineDetails}
                    required={element.required}
                    choices={element.choices}
                    minChoices={element.minChoices}
                    maxChoices={element.maxChoices}
                    size={element.size}
                    validationMessages={validationMessages}
                    createAnswer={createAnswer}
                    answerValue={answer?.type === 'image-select' ? answer?.value : null}
                    withCachedImageUrl={props.withCachedImageUrl}
                    allowZoom={props.allowImageZoom}
                    openOptions={openOptions}
                />
            )
        case 'photo':
            return (
                <ElementCamera
                    deps={props.deps}
                    key={element.id}
                    elementId={element.id}
                    loopPathIds={loopPathIds}
                    label={element.label}
                    timelineDetails={timelineDetails}
                    required={element.required}
                    validationMessages={validationMessages}
                    createAnswer={createAnswer}
                    mediaType={element.kind ?? 'photo'}
                    answer={answer?.type === 'photo' || answer?.type === 'extensible-files' ? answer : null}
                    allowGallerySelection={element.allowGallerySelection}
                    savePathForRecovery={() => props.savePathForRecovery(element.id)}
                    recovery={props.recovery}
                    cordovaGetMedia={props.cordovaGetMedia}
                    selectFile={props.selectFile}
                    saveBlob={props.saveBlob}
                    saveFile={props.saveFile}
                    cancelUpload={props.cancelUpload}
                    onShowPreviewChange={props.onShowPreviewChange}
                    browserCameraDeviceIndexLookup={props.browserCameraDeviceIndexLookup}
                    kind={element.kind}
                    fileUrlStates={props.fileUrlStates}
                    promiseThunkQueue={props.promiseThunkQueue}
                    forceUseBrowserCameraAPI={props.forceUseBrowserCameraAPI}
                    isIOS={props.isIOS}
                    isAndroid={props.isAndroid}
                    openOptions={openOptions}
                />
            )

        case 'confirmation':
            return (
                <ElementConfirmation
                    deps={props.deps}
                    key={element.id}
                    elementId={element.id}
                    loopPathIds={loopPathIds}
                    label={element.label}
                    timelineDetails={timelineDetails}
                    required={element.required}
                    validationMessages={validationMessages}
                    createAnswer={createAnswer}
                    answerValue={answer?.type === 'confirmation' ? answer?.value : null}
                    openOptions={openOptions}
                />
            )

        case 'calculation':
            return (
                <ElementCalculation
                    deps={props.deps}
                    key={element.id}
                    elementId={element.id}
                    loopPathIds={loopPathIds}
                    label={element.label}
                    validationMessages={validationMessages}
                    calculationValue={findCalculationResult(props.flowSummary, element.id, loopPathIds)?.value}
                    decimalPlaces={element.decimalPlaces ?? 2}
                    openOptions={openOptions}
                />
            )

        case 'text-capture':
            return (
                <ElementTextCapture
                    deps={props.deps}
                    key={element.id}
                    elementId={element.id}
                    loopPathIds={loopPathIds}
                    label={element.label}
                    timelineDetails={timelineDetails}
                    required={element.required}
                    multipleLines={element.multipleLines}
                    validationMessages={validationMessages}
                    createAnswer={createAnswer}
                    answerValue={answer?.type === 'text-capture' ? answer?.value : null}
                    openOptions={openOptions}
                />
            )
        case 'image-content':
            return (
                <ElementImageContent
                    deps={props.deps}
                    key={element.id}
                    elementId={element.id}
                    loopPathIds={loopPathIds}
                    label={element.label}
                    url={element.url}
                    annotationFileId={element.annotationFileId}
                    withCachedImageUrl={props.withCachedImageUrl}
                    allowZoom={props.allowImageZoom}
                    openOptions={openOptions}
                />
            )

        case 'text-content':
            return (
                <ElementTextContent
                    key={element.id}
                    deps={props.deps}
                    element={element}
                    loopPathIds={loopPathIds}
                    subjectData={props.subjectData}
                    openOptions={openOptions}
                />
            )

        case 'datetime':
            //Default answer for a date-time question within a guide attached to a loop element
            //is not present in the answer and must be fetched from props.answers. This change works but will be investigated further in Zap-4953
            const answer_ = answer || props.answers.filter((answer) => answer.element === element.id)?.[0]
            return (
                <ElementDateTime
                    deps={props.deps}
                    key={element.id}
                    elementId={element.id}
                    loopPathIds={loopPathIds}
                    label={element.label}
                    timelineDetails={timelineDetails}
                    required={element.required}
                    selectType={element.selectType}
                    validationMessages={validationMessages}
                    createAnswer={createAnswer}
                    initialValue={answer_?.type === 'datetime' ? answer_?.value : null}
                    reportStartDate={props.reportStartDate}
                    openOptions={openOptions}
                />
            )

        case 'section':
            return (
                <ElementSection
                    deps={props.deps}
                    key={element.id}
                    elementId={element.id}
                    loopPathIds={loopPathIds}
                    label={element.label}
                    required={element.required}
                    validationMessages={validationMessages}
                    routeToSection={props.routeToSection}
                    answerValue={answer?.type === 'section' ? answer?.value : null}
                    isInProgress={getIsInProgress(props.flowSummary, element.id, loopPathIds)}
                />
            )

        case 'signature':
            return (
                <ElementSignature
                    deps={props.deps}
                    key={element.id}
                    elementId={element.id}
                    loopPathIds={loopPathIds}
                    label={element.label}
                    timelineDetails={timelineDetails}
                    required={element.required}
                    validationMessages={validationMessages}
                    createAnswer={createAnswer}
                    answerValue={answer?.type === 'signature' ? answer?.value : null}
                    saveBlob={props.saveBlob}
                    cancelUpload={props.cancelUpload}
                    fileUrlStates={props.fileUrlStates}
                    promiseThunkQueue={props.promiseThunkQueue}
                    openOptions={openOptions}
                />
            )

        case 'dropdown':
            const answerValue = answer?.type === 'dropdown' ? answer?.value : null

            // Single select case
            if (element.maxChoices === 1) {
                return (
                    <ElementDropdown
                        deps={props.deps}
                        key={element.id}
                        elementId={element.id}
                        loopPathIds={loopPathIds}
                        label={element.label}
                        timelineDetails={timelineDetails}
                        required={element.required}
                        choices={element.choices}
                        validationMessages={validationMessages}
                        createAnswer={createAnswer}
                        answerValue={answerValue}
                        openOptions={openOptions}
                    />
                )
            } else {
                // multi select case
                return (
                    <ElementMultiselectDropdown
                        deps={props.deps}
                        key={element.id}
                        elementId={element.id}
                        loopPathIds={loopPathIds}
                        label={element.label}
                        timelineDetails={timelineDetails}
                        required={element.required}
                        choices={element.choices}
                        maxChoices={element.maxChoices}
                        validationMessages={validationMessages}
                        createAnswer={createAnswer}
                        answerValue={answerValue}
                        openOptions={openOptions}
                    />
                )
            }

        case 'files-select':
            return (
                <ElementFilesSelect
                    deps={props.deps}
                    key={element.id}
                    elementId={element.id}
                    loopPathIds={loopPathIds}
                    label={element.label}
                    uploadLimit={element.uploadLimit}
                    timelineDetails={timelineDetails}
                    required={element.required}
                    validationMessages={validationMessages}
                    createAnswer={createAnswer}
                    uploads={props.uploads}
                    allowedFileExtensions={element.fileTypes}
                    selectFile={props.selectFile}
                    showProgress={props.showFileProgress}
                    cancelUpload={props.cancelUpload}
                    answerValue={answer?.type === 'files-select' ? answer?.value : null}
                    filesMetaDataMap={props.filesMetaDataMap}
                    promiseThunkQueue={props.promiseThunkQueue}
                    openOptions={openOptions}
                />
            )

        case 'unit-select':
            return (
                <React.Fragment key={element.id}>
                    {promiseStateRender(props.deps, props.allUnitsState, (allUnits) => (
                        <ElementUnitSelect
                            deps={props.deps}
                            key={element.id}
                            elementId={element.id}
                            loopPathIds={loopPathIds}
                            label={element.label}
                            timelineDetails={timelineDetails}
                            required={element.required}
                            unitIds={props.flowUnitIds[element.id]}
                            allUnits={allUnits}
                            maxUnits={element.maxUnits}
                            validationMessages={validationMessages}
                            createAnswer={createAnswer}
                            renderCachedImage={props.renderCachedImage}
                            answerValue={answer?.type === 'unit-select' ? answer?.value : null}
                            unitTypes={element.unitTypes}
                            barcodeScanForUnits={props.barcodeScanForUnits}
                            openOptions={openOptions}
                        />
                    ))}
                </React.Fragment>
            )

        case 'video-content':
            return (
                <ElementVideoContent
                    deps={props.deps}
                    key={element.id}
                    elementId={element.id}
                    loopPathIds={loopPathIds}
                    label={element.label}
                    url={element.url}
                    isOnline={props.deps.isOnline ?? (() => Promise.resolve(true))}
                    openOptions={openOptions}
                />
            )
        case 'link':
            return (
                <ElementLink
                    deps={props.deps}
                    key={element.id}
                    elementId={element.id}
                    loopPathIds={loopPathIds}
                    description={element.label}
                    url={element.url}
                    openOptions={openOptions}
                />
            )

        case 'flow':
            const workflow = findById(props.workflows, element.workflow)

            return (
                <ElementSubtask
                    deps={props.deps}
                    elementId={element.id}
                    loopPathIds={loopPathIds}
                    elementLabel={element.label}
                    procedureId={element.procedure}
                    key={element.id}
                    validationMessages={validationMessages}
                    disableEdit={props.reportEditDetails !== null}
                    required={element.required}
                    minReports={element.minReports}
                    maxReports={element.maxReports}
                    subtaskReports={props.subtaskReports}
                    createSubtask={async () => {
                        const subtaskSubjectUnitId = await findInferredSubtaskSubjectUnit(
                            element,
                            workflow.flow.subjectType,
                            props.subjectUnit,
                            props.loopPathIds,
                            props.fetchUnitsByIds
                        )

                        return props.createSubtask({
                            procedureId: element.procedure,
                            workflowId: element.workflow,
                            parentClientId: props.reportId,
                            progenitorClientId: props.progenitorId,
                            subjectUnitId: subtaskSubjectUnitId,
                            parentSubjectUnitId: props.subjectUnit?.id,
                        })
                    }}
                    deleteSubtask={props.deleteSubtask}
                    routeToSubtask={props.routeToSubtask}
                    createAnswer={createAnswer}
                    answerValue={answer?.type === 'reports' ? answer?.value : undefined}
                    isWorkflowDeleted={workflow.isDeleted}
                    isGuide={workflow.type === 'guide'}
                    openOptions={openOptions}
                    featureEnabled={props.featureEnabled}
                    hasAccessToProcedure={props.hasAccessToProcedure}
                />
            )

        case 'loop':
            return (
                <React.Fragment key={element.id}>
                    <ElementLoop
                        deps={props.deps}
                        element={element}
                        renderSteps={(steps, loopPathIds, subjectData) => (
                            <Steps
                                {...props}
                                loopPathIds={loopPathIds}
                                steps={steps}
                                subjectData={subjectData}
                                isLoop
                            />
                        )}
                        loopPathIds={loopPathIds}
                        loopPlan={props.loopPlan}
                        cachedUnitData={props.cachedUnitData}
                        allUnitsState={props.allUnitsState}
                        withCachedImageUrl={props.withCachedImageUrl}
                        timelineDetails={timelineDetails}
                        answers={props.flowSummary.answers}
                        createAnswer={props.createAnswer}
                        createAnswerBase={props.createAnswerBase}
                        removeAnswers={props.removeAnswers}
                        renderCachedImage={props.renderCachedImage}
                        barcodeScanForUnits={props.barcodeScanForUnits}
                        fetchUnitsByType={props.fetchUnitsByType}
                        fetchUnitType={props.fetchUnitType}
                        fetchUnitTypeName={props.fetchUnitTypeName}
                        showValidation={props.showValidation}
                        flowSummary={props.flowSummary}
                        scheduleParameters={props.scheduleParameters}
                    />
                </React.Fragment>
            )

        case 'guide':
            const fetchFreshFileGuideDownloadUrl = (fileId: FileId): Promise<AppFileGuideDetails> =>
                props.fetchFileGuideDetailsMap([fileId]).then((detailsMap) => detailsMap.get(fileId))
            const getAnswerValue = (guides: AppGuide[]): Partial<Flows.GuideAnswerValue[]> => {
                if (Flows.isAnswerOfType('guide', answer)) {
                    return Array.isArray(answer.value) ? answer.value : [answer.value]
                }

                return guides.map((guide) => {
                    return {
                        type: guide?.variant,
                        guideId: guide?.id,
                        completed: false,
                        version: guide?.version,
                    }
                })
            }

            const fetchAppGuides = async (elementGuideContext: Promise<ElementGuideContext>): Promise<AppGuide[]> => {
                const context = await elementGuideContext
                if (element.dynamicGuideSettings) {
                    return filterGuidesByTags(element, context, props.subjectUnit, props.loopPathIds)
                } else {
                    return [context.guidesMap.get(element.guideId)]
                }
            }

            return props.elementGuideContext
                .map((elementGuideContext) => (
                    <ElementGuide
                        deps={props.deps}
                        elementId={element.id}
                        fetchAppGuides={() => fetchAppGuides(elementGuideContext)}
                        fetchFileGuideDetails={(fileId, fresh) =>
                            fresh
                                ? fetchFreshFileGuideDownloadUrl(fileId)
                                : elementGuideContext.then((context) => context.fileGuideDetailsMap.get(fileId))
                        }
                        label={element.label}
                        loopPathIds={loopPathIds}
                        onOpenFile={props.onOpenFile}
                        createAnswer={createAnswer}
                        getAnswerValue={getAnswerValue}
                        renderCachedImage={props.renderCachedImage}
                        onClickGuideParentInfo={props.onClickGuideParentInfo}
                        required={element.required}
                        routeToGuideExecutionPage={(guide) =>
                            props.routeToGuideExecutionPage(guide, {
                                guideElementId: element.id,
                                parentClientId: props.reportId,
                                parentScrollTop: props.getScrollPosition(),
                                loopPathIds,
                                guideId: guide,
                            })
                        }
                        validationMessages={validationMessages}
                        allowImageZoom={props.allowImageZoom}
                        openOptions={openOptions}
                    />
                ))
                .withDefault(
                    // Element guide not ready to be rendered without context.
                    <></>
                )

        default:
            never(element)
            return <div key={(element as any).id} />
    }
}
